import type { EvalSet } from '@agentisai/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { uploadFileToS3 } from '../lib/uploadToS3';
import {
  beginEvalDocumentUpload,
  deleteEvalDocument,
  deleteEvalSet,
  getEvalDocumentText,
  getEvalSet,
  listEvalDocuments,
  listEvalSets,
  putEvalDocumentText,
  putEvalSet,
} from './client';
import { keys } from './keys';

/**
 * Eval question sets + documents (ADR-0073). Reads wait on `enabled` (the
 * caller's `/me` grant) so a user without `evals:read` never fires a request
 * that would only 403. Writes use pending-UI + invalidate (frontend-architecture
 * R7): the editor stays put until the server confirms, so there is nothing to
 * roll back, and a 409 (someone saved first) surfaces as the mutation's error.
 */

export function useEvalSets(enabled: boolean) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.evals.sets(),
    queryFn: ({ signal }) => listEvalSets({ getAccessToken, signal }),
    enabled,
  });
}

export function useEvalSet(name: string | undefined, enabled: boolean) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.evals.set(name ?? ''),
    queryFn: ({ signal }) => getEvalSet(name ?? '', { getAccessToken, signal }),
    enabled: enabled && name !== undefined,
    // The ETag is what a save is conditional on — never refetch it out from
    // under an open editor, or a save would silently overwrite the refetched copy.
    refetchOnWindowFocus: false,
  });
}

export function useSaveEvalSet() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.evals.saveSet(),
    mutationFn: ({ set, etag }: { set: EvalSet; etag: string | null }) =>
      putEvalSet(set.name, { set, etag }, { getAccessToken }),
    onSuccess: (saved) => queryClient.setQueryData(keys.evals.set(saved.data.set.name), saved),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: keys.evals.sets(), exact: true }),
  });
}

export function useDeleteEvalSet() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.evals.deleteSet(),
    mutationFn: (name: string) => deleteEvalSet(name, { getAccessToken }),
    onSuccess: (_data, name) => queryClient.removeQueries({ queryKey: keys.evals.set(name) }),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: keys.evals.sets(), exact: true }),
  });
}

export function useEvalDocuments(enabled: boolean) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.evals.documents(),
    queryFn: ({ signal }) => listEvalDocuments({ getAccessToken, signal }),
    enabled,
  });
}

export function useEvalDocumentText(name: string | undefined, enabled: boolean) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.evals.document(name ?? ''),
    queryFn: ({ signal }) => getEvalDocumentText(name ?? '', { getAccessToken, signal }),
    enabled: enabled && name !== undefined,
    refetchOnWindowFocus: false,
  });
}

export function useSaveEvalDocumentText() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.evals.saveDocument(),
    mutationFn: ({ name, text, etag }: { name: string; text: string; etag: string }) =>
      putEvalDocumentText(name, { text, etag }, { getAccessToken }),
    onSuccess: (saved) => queryClient.setQueryData(keys.evals.document(saved.data.name), saved),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: keys.evals.documents(), exact: true }),
  });
}

export function useDeleteEvalDocument() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.evals.deleteDocument(),
    mutationFn: (name: string) => deleteEvalDocument(name, { getAccessToken }),
    onSuccess: (_data, name) => queryClient.removeQueries({ queryKey: keys.evals.document(name) }),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: keys.evals.documents(), exact: true }),
  });
}

/** Sign → POST straight to S3 (the Files-rail upload shape). Same name = replace. */
export function useUploadEvalDocument() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.evals.uploadDocument(),
    mutationFn: async (file: File) => {
      const { data } = await beginEvalDocumentUpload(file.name, { getAccessToken });
      await uploadFileToS3(data.url, data.fields, file);
    },
    onSuccess: (_data, file) =>
      queryClient.removeQueries({ queryKey: keys.evals.document(file.name) }),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: keys.evals.documents(), exact: true }),
  });
}
